import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function HelpSystemDialogShell({
	open,
	onClose,
	title,
	subtitle,
	eyebrow,
	sections,
	children,
	footerNote,
}) {
	const contentRef = useRef(null);
	const [activeSectionId, setActiveSectionId] = useState(sections?.[0]?.id ?? "");

	const sectionLookup = useMemo(
		() => new Set((sections || []).map((section) => section.id)),
		[sections],
	);

	useEffect(() => {
		if (open) {
			setActiveSectionId(sections?.[0]?.id ?? "");
		}
	}, [open, sections]);

	const handleScrollToSection = (sectionId) => {
		if (!sectionLookup.has(sectionId)) {
			return;
		}

		setActiveSectionId(sectionId);
		const sectionElement = contentRef.current?.querySelector(`[data-help-section="${sectionId}"]`);
		if (sectionElement) {
			sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullScreen
			PaperProps={{
				sx: {
					overflow: "hidden",
					backgroundImage:
						"linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 100%)",
				},
			}}
		>
			<div className="flex h-full min-h-0 flex-col text-slate-900">
				<div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-emerald-900 via-emerald-800 to-slate-900 px-5 py-5 text-white md:px-7">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0">
							<h2 className="mt-2 text-2xl font-bold leading-tight md:text-3xl">{title}</h2>
							<p className="mt-3 max-w-3xl text-sm leading-6 text-emerald-50/95 md:text-[15px]">
								{subtitle}
							</p>
						</div>
						<IconButton
							onClick={onClose}
							sx={{
								color: "#275d38",
								border: "1px solid rgba(255,255,255,0.18)",
								backgroundColor: "white",
								"&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
							}}
						>
							<CloseIcon />
						</IconButton>
					</div>
					<div className="mt-4 flex gap-2 overflow-x-auto pb-1 lg:hidden">
						{sections.map((section) => {
							const isActive = activeSectionId === section.id;

							return (
								<button
									key={section.id}
									type="button"
									onClick={() => handleScrollToSection(section.id)}
									className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
										isActive
											? "border-white bg-white text-emerald-900"
											: "border-white/20 bg-white/10 text-white hover:bg-white/15"
									}`}
								>
									{section.navLabel || section.title}
								</button>
							);
						})}
					</div>
				</div>

				<div className="flex min-h-0 flex-1">
					<aside className="hidden w-72 shrink-0 border-r border-slate-200 bg-white/85 p-5 lg:flex lg:flex-col">
						<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
							Notable Changes
						</p>
						<div className="mt-4 flex flex-col gap-2">
							{sections.map((section) => {
								const isActive = activeSectionId === section.id;

								return (
									<button
										key={section.id}
										type="button"
										onClick={() => handleScrollToSection(section.id)}
										className={`rounded-2xl border px-4 py-3 text-left transition ${
											isActive
												? "border-emerald-200 bg-emerald-50 text-emerald-900 shadow-sm"
												: "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
										}`}
									>
										<p className="text-sm font-semibold">{section.navLabel || section.title}</p>
										{section.summary ? (
											<p className="mt-1 text-xs leading-5 text-slate-500">{section.summary}</p>
										) : null}
									</button>
								);
							})}
						</div>
					</aside>

					<div
						ref={contentRef}
						className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6"
					>
						<div className="mx-auto flex max-w-[1400px] flex-col gap-5">{children}</div>
					</div>
				</div>
			</div>
		</Dialog>
	);
}
